import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IDatagridSettings, IDocumentWithDetails } from '@core-lib/models';
import { DocumentSupportedType } from '@core-lib/enums';
import { IDocumentUploadResponse, INatureAffairePieceJointe } from '@core-lib/models/affaire.model';
import { AuthSharedService } from '@core-lib/services/auth.service';
import * as SharedStoreSelectors from '@core-lib/store/shared-store/selectors';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DocumentMetadata, IDocumentsFilesForm, IDocumentViewerUrl, PageDocument } from '../models/alfresco.model';
import { RessourceType } from '@core-lib/enums/ressource-type.enum';
import { IRessourceLight, IRessourceLightList } from '../models/ressource.model';
import { FileSearchSortableCols } from 'projects/app-back/src/app/admin-module/models';

@Injectable()
export class DocumentGedApiService {
	get tenantKey() {
		return this.authService.currentTenantKey;
	}

	typesPieceJointe$: Observable<INatureAffairePieceJointe[]> = this.store$.select(SharedStoreSelectors.selectTypesDocuments);

	constructor(
		protected store$: Store,
		private http: HttpClient,
		private authService: AuthSharedService,
		private toastr: ToastrService,
		private translateService: TranslateService
	) {}

	// Todo: Appeler [Delete] Document/<Type>
	deleteDocumentsByIds(documentIds: string[], documentType: string = ''): Observable<boolean> {
		if (documentIds.length > 0) {
			return this.http
				.post<string[]>(`${environment.apiUrl}/t/${this.tenantKey}/document/${documentType}/delete/list`, documentIds)
				.pipe(map(() => true));
		}
		return EMPTY.pipe(map(() => false));
	}

	/**
	 * Cette Methode utilise DocumentStore lorsque documentType est renseigné, sinon elle utilise l'ancienne API Alfresco. Utilise de préférence cette methode pour supprimer un document.
	 * @param documentId
	 * @param documentType c'est le DocumentSupportedType
	 * @returns
	 */
	deleteDocumentsById(documentId: string, documentType: string = ''): Observable<boolean> {
		if (documentId.trim() !== '') {
			const apiMethod = `document/${documentId}/${documentType}`;
			return this.http.delete<string[]>(`${environment.apiUrl}/t/${this.tenantKey}/${apiMethod}`, {}).pipe(map(() => true));
		}
		return EMPTY.pipe(map(() => false));
	}

	getCBDocuments(operationId?: string, coordonneeBancaireId?: string, compteId?: string): Observable<DocumentMetadata[]> {
		if (operationId != null) {
			return this.http.get<DocumentMetadata[]>(
				`${environment.apiUrl}/t/${this.tenantKey}/document/coordonnees-bancaires/${coordonneeBancaireId}/operation/${operationId}/search`
			);
		} else {
			return this.http.get<DocumentMetadata[]>(
				`${environment.apiUrl}/t/${this.tenantKey}/document/coordonnees-bancaires/${coordonneeBancaireId}/compte/search`
			);
		}
	}

	getLotDocuments(lotId?: string): Observable<DocumentMetadata[]> {
		return this.http.get<DocumentMetadata[]>(`${environment.apiUrl}/t/${this.tenantKey}/document/lots/${lotId}/search`);
	}

	getCBDocumentsAsJson(coordonneeBancaireId: string): Observable<any> {
		return this.http.get(
			`${environment.apiUrl}/t/${this.tenantKey}/document/coordonnees-bancaires/${coordonneeBancaireId}/searchAsJson`,
			{ responseType: 'json' } // On spécifie que la réponse attendue est en JSON
		);
	}

	getOperationDocuments(operationId?: string, pageSize?: number, pageIndex?: number, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('operationId', operationId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/document/operation/${operationId}/search`, {
			params: httpParams
		});
	}
	getInstructionPermanenteDocuments(instructionPermanenteId?: string): Observable<DocumentMetadata[]> {
		return this.http.get<DocumentMetadata[]>(`${environment.apiUrl}/t/${this.tenantKey}/document/instruction-permanente/${instructionPermanenteId}/search`);
	}
	
	loadTypesPieceJointe(): Observable<INatureAffairePieceJointe[]> {
		return this.http.get<INatureAffairePieceJointe[]>(`${environment.apiUrl}/t/${this.tenantKey}/document/referentielTypesPJ`);
	}

	getTypesPieceJointeByNature(natureId: string): Observable<INatureAffairePieceJointe[]> {
		return this.http.get<INatureAffairePieceJointe[]>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/typesPJByNature?natureAffaireId=${natureId}`
		);
	}

	getTypesPieceJointeByAffaire(affaireId: string): Observable<INatureAffairePieceJointe[]> {
		return this.http.get<INatureAffairePieceJointe[]>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/typesPJByAffaire?affaireId=${affaireId}`
		);
	}

	getAffaireDocumentsByType(affaireId: string, type: string, pageSize: number, pageIndex: number): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('affaireId', affaireId);
		httpParams = httpParams.append('type', type);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/alfresco/affaire/${affaireId}/${type}`, {
			params: httpParams
		});
	}

	getCompteDocuments(compteId: string, pageSize: number, pageIndex: number, includeViewer: boolean = false, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('compteId', compteId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		httpParams = httpParams.append('includeViewer', includeViewer);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/document/compte/${compteId}/search`, {
			params: httpParams
		});
	}

	getAffaireDocuments(affaireId: string, pageSize: number, pageIndex: number, includeViewer: boolean = false, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('affaireId', affaireId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		httpParams = httpParams.append('includeViewer', includeViewer);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/document/affaire/${affaireId}/search`, {
			params: httpParams
		});
	}

	getRecueilSignatureDocuments(
		signatureId: string,
		pageSize: number,
		pageIndex: number,
		includeViewer: boolean = false,
		sortBy?: string
	): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		httpParams = httpParams.append('includeViewer', includeViewer);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/${DocumentSupportedType.RecueilSignature}/${signatureId}/search`,
			{
				params: httpParams
			}
		);
	}

	getEventDocuments(eventId: string, pageSize: number, pageIndex: number, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('eventId', eventId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/document/evenement/${eventId}/search`, {
			params: httpParams
		});
	}

	getDelegationBancaireDocuments(delegationBancaireId: string, pageSize: number, pageIndex: number, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('delegationBancaireId', delegationBancaireId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/${DocumentSupportedType.DelegationBancaire}/${delegationBancaireId}/search`,
			{
				params: httpParams
			}
		);
	}

	getCourrierDocuments(courrierId: string, pageSize: number, pageIndex: number, sortBy?: string): Observable<PageDocument> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('courrierId', courrierId);
		httpParams = httpParams.append('pageSize', pageSize);
		httpParams = httpParams.append('pageIndex', pageIndex);
		if (sortBy) {
			httpParams = httpParams.append('sortBy', sortBy);
		}
		return this.http.get<PageDocument>(`${environment.apiUrl}/t/${this.tenantKey}/document/courrier/${courrierId}/search`, {
			params: httpParams
		});
	}

	uploadRecueilSignatureFile(
		form: IDocumentsFilesForm,
		signatureId: string,
		compteId: string
	): Observable<IDocumentUploadResponse> {
		const formData = new FormData();
		this.appendDocsWithoutTypeToFormData(formData, form.documents.filesList);

		return this.http.post<any>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/${DocumentSupportedType.RecueilSignature}/${signatureId}/upload/list/${compteId}`,
			formData
		);
	}

	uploadCompteFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for operation
		this.appendDocsToFormData(formData, form.documents.filesList);
		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/compte/${form.compteId}/upload/list`, formData);
	}

	uploadInstructionPermanenteFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for IP
		this.appendDocsToFormData(formData, form.documents.filesList);
		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/instruction-permanente/${form.instructionPermanenteId}/upload/list`, formData);
	}

	uploadAffaireFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for operations
		this.appendDocsToFormData(formData, form.documents.filesList);

		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/affaire/${form.affaireId}/upload/list`, formData);
	}

	uploadEvenementFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for event
		this.appendDocsWithoutTypeToFormData(formData, form.documents.filesList);
		return this.http.post<any>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/evenement/${form.evenementId}/upload/list`,
			formData
		);
	}

	uploadDelegationBancaireFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for compte Bancaire
		this.appendDocsWithoutTypeToFormData(formData, form.documents.filesList);
		return this.http.post<any>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/delegations-bancaires/${form.delegationBancaireId}/upload/list`,
			formData
		);
	}

	uploadCourrierFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for courrier
		this.appendDocsToFormData(formData, form.documents.filesList);
		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/courrier/${form.courrierId}/upload/list`, formData);
	}

	// TODO: Create a strongly typed parameter
	uploadOperationFile(form: any): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for "Coordonnées Bancaires"
		if (form.coordonneBancaireId) {
			for (let i = 0; i < form?.documents?.length; i++) {
				formData.append('files', form.documents[i]);
			}
			return this.http.post<any>(
				`${environment.apiUrl}/t/${this.tenantKey}/document/coordonnees-bancaires/${form.coordonneBancaireId}/retrait/upload/list?retraitId=${form.operationId}`,
				formData
			);
		}

		// Upload documents for operations
		this.appendDocsToFormData(formData, form.documents.filesList as IDocumentWithDetails[]);

		return this.http.post<any>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/operation/${form.operationId}/upload/list`,
			formData
		);
	}

	uploadCoordBancaireFile(form: any, isRetraitMode: boolean): Observable<IDocumentUploadResponse> {
		const formData = new FormData();
		console.log('form in uploadCoordBancaireFile', form);
		console.log('isRetraitMode', isRetraitMode);
		// Upload documents for "Coordonnées Bancaires"
		if (form.coordonneBancaireId) {
			for (let i = 0; i < form?.documents?.length; i++) {
				formData.append('files', form.documents[i]);
			}
			const subUrl = isRetraitMode ? `retrait/upload/list?retraitId=${form.operationId}` : `compte/upload/list`;
			return this.http.post<any>(
				`${environment.apiUrl}/t/${this.tenantKey}/document/coordonnees-bancaires/${form.coordonneBancaireId}/${subUrl}`,
				formData
			);
		}
	}

	uploadLotFile(form: any): Observable<IDocumentUploadResponse> {
		const formData = new FormData();
		this.appendDocsToFormData(formData, form.documents.filesList as IDocumentWithDetails[]);
		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/lots/${form.lotId}/upload/list`, formData);
	}

	uploadOperationFileCommentaire(form: any): Observable<any> {
		const formData = new FormData();
		// Upload documents for comment
		for (let i = 0; i < form?.documents?.filesList?.length; i++) {
			formData.append(`documents[${i}][file]`, form.documents?.filesList[i].file);
			// Libellé/commentaire facultatifs : éviter que FormData ne sérialise null en "null".
			formData.append(`documents[${i}][libelle]`, form.documents?.filesList[i].libelle ?? '');
			formData.append(`documents[${i}][commentaire]`, form.documents?.filesList[i].commentaire ?? '');
			formData.append(`documents[${i}][typePieceJointeId]`, form.documents?.filesList[i].typePieceJointeId);
		}
		return this.http.post<any>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/commentaire/${form.commentaireId}/upload/list`,
			formData
		);
	}

	/**
	 * Afficher une liste des nom des types des documents celon les Ids fournit dans l'input, séparés par un ";"
	 * @param documentMetadata : Liste des metadates d'une opération
	 * @returns Observable<string>: Liste des nom des types des documents celon les Ids fournit dans l'input separé par un
	 */
	documentTooltipText(documentMetadata: DocumentMetadata[]): string {
		// return 'Liste de types des documents';
		const distinctTypes = [...new Set(documentMetadata?.map(item => item?.typePieceJointe))]; // [ 'A', 'B']

		let typesNames: string[] = [];
		distinctTypes?.forEach(element => {
			typesNames.push(element);
		});

		return typesNames.length > 0 ? typesNames?.sort()?.join('; ') : 'Aucun document';
	}

	/**
	 * Télécharge le contenu d'un document du DocumentStore.
	 * Le documentType est obligatoire : l'ancienne route Alfresco (`alfresco/document/<id>`) est supprimée.
	 * @param documentType c'est le DocumentSupportedType
	 */
	downloadDocument(documentId: string, fileName: string, documentType: string) {
		const endPointApi = `document/${documentId}/${documentType}/content`;
		const url = this.authService.isSuperAdmin
			? `${environment.apiUrl}/CommonAlfresco/document/${documentId}`
			: `${environment.apiUrl}/t/${this.tenantKey}/${endPointApi}`;
		this.toastr.warning(this.translateService.instant('Documents.DownloadDocument.DownloadIsRunning'));
		this.http
			.get(url, {
				responseType: 'blob' // Set the response type to 'blob'
			})
			.subscribe({
				next: (response: any) => {
					this.toastr.info(this.translateService.instant('Documents.DownloadDocument.DownloadSuccess'));
					const blob = new Blob([response], { type: 'application/octet-stream' });
					fileName = fileName ?? 'MyDocument.docx';

					const downloadLink = document.createElement('a');
					downloadLink.href = URL.createObjectURL(blob);
					downloadLink.download = fileName;
					downloadLink.click();

					URL.revokeObjectURL(downloadLink.href); // Clean up the object URL
				},
				error: (err: Error) => {
					this.toastr.error(err.message);
				}
			});
	}
	downloadModeleDocumentContent(modeleId: string, fileName: string, closeWindowAfter?: boolean) {
		const page = `ressources/modele-document/${modeleId}/content`;
		const url = `${environment.apiUrl}/${page}`;
		this.toastr.warning(this.translateService.instant('Documents.DownloadDocument.DownloadIsRunning'));
		this.downloadContentBase(
			url,
			this.translateService.instant('Documents.DownloadDocument.DownloadSuccess'),
			closeWindowAfter,
			fileName
		);
	}
	downloadMessageContent(messageId: string, fileName: string, closeWindowAfter?: boolean) {
		const page = `ressources/message-attachment/${messageId}/${fileName}/content`;
		const url = `${environment.apiUrl}/${page}`;
		this.toastr.warning(this.translateService.instant('Documents.DownloadDocument.DownloadIsRunning'));
		this.downloadContentBase(
			url,
			this.translateService.instant('Documents.DownloadDocument.DownloadSuccess'),
			closeWindowAfter,
			fileName
		);
	}

	uploadCarpaFile(form: IDocumentsFilesForm): Observable<IDocumentUploadResponse> {
		const formData = new FormData();

		// Upload documents for operations
		this.appendDocsToFormData(formData, form.documents.filesList);

		return this.http.post<any>(`${environment.apiUrl}/t/${this.tenantKey}/document/documentsCarpa/upload`, formData);
	}

	private downloadContentBase(url: string, toastrDownloadSuccess?: string, closeWindowAfter?: boolean, defaultFileName?: string) {
		this.http
			.get(url, {
				observe: 'response', // Permet d'accéder aux headers
				responseType: 'blob' // Set the response type to 'blob'
			})
			.subscribe({
				next: response => {
					if (toastrDownloadSuccess) this.toastr.info(toastrDownloadSuccess);
					const blob = new Blob([response.body], { type: response.body.type });
					const contentDisposition = response.headers.get('content-disposition');
					let fileName = defaultFileName;

					if (contentDisposition) {
						const matches = contentDisposition.match(/filename="?(.+?)"?$/);
						fileName = matches ? matches[1] : fileName;
					}

					const downloadLink = document.createElement('a');
					downloadLink.href = URL.createObjectURL(blob);
					downloadLink.download = decodeURIComponent(fileName);
					downloadLink.click();

					URL.revokeObjectURL(downloadLink.href); // Clean up the object URL
					if (closeWindowAfter) {
						setTimeout(() => {
							window.close();
						}, 1000);
					}
				},
				error: (err: Error) => {
					this.toastr.error(err.message);
				}
			});
	}

	private appendDocsToFormData(formData: FormData, filesList: IDocumentWithDetails[]): FormData {
		for (let i = 0; i < filesList?.length; i++) {
			formData.append(`documents[${i}][file]`, filesList[i].file);
			// FormData.append() sérialise null/undefined en la chaîne "null"/"undefined".
			// Libellé et commentaire sont facultatifs : on envoie une chaîne vide quand ils ne sont pas renseignés.
			formData.append(`documents[${i}][libelle]`, filesList[i].libelle ?? '');
			formData.append(`documents[${i}][commentaire]`, filesList[i].commentaire ?? '');
			formData.append(`documents[${i}][typePieceJointeId]`, filesList[i].typePieceJointeId);
		}

		return formData;
	}

	private appendDocsWithoutTypeToFormData(formData: FormData, filesList: IDocumentWithDetails[]): FormData {
		for (let i = 0; i < filesList?.length; i++) {
			formData.append(`documents[${i}][file]`, filesList[i].file);
			// FormData.append() sérialise null/undefined en la chaîne "null"/"undefined".
			// Libellé et commentaire sont facultatifs : on envoie une chaîne vide quand ils ne sont pas renseignés.
			formData.append(`documents[${i}][libelle]`, filesList[i].libelle ?? '');
			formData.append(`documents[${i}][commentaire]`, filesList[i].commentaire ?? '');
			if (filesList[i].typePieceJointeId != null) {
				formData.append(`documents[${i}][typePieceJointeId]`, filesList[i].typePieceJointeId);
			}
		}
		return formData;
	}

	/********** Nouvelle gestion des documents avec le documentStore */
	getDocumentCarpaUrl(documentId: string): Observable<string> {
		return this.http.get(`${environment.apiUrl}/t/${this.tenantKey}/documentViewer/documentCarpa/${documentId}/documentUrl`, {
			responseType: 'text'
		});
	}

	getDocumentViewerUrl(documentStoreId: string, documentType: string): Observable<IDocumentViewerUrl> {
		return this.http.get<IDocumentViewerUrl>(
			`${environment.apiUrl}/t/${this.tenantKey}/document/${documentStoreId}/${documentType}/viewer`
		);
	}

	deleteDocumentCarpa(documentStoreId: string): Observable<boolean> {
		return this.http
			.delete<string[]>(`${environment.apiUrl}/t/${this.tenantKey}/document/documentCarpa/${documentStoreId}`, {})
			.pipe(map(() => true));
	}

	loadRessources(type: RessourceType): Observable<IRessourceLight[]> {
		return this.http.get<IRessourceLight[]>(`${environment.apiUrl}/ressources/${type}/load`);
	}

	searchRessources(
		settings: IDatagridSettings<FileSearchSortableCols>,
		type?: RessourceType,
		key?: string
	): Observable<IRessourceLightList> {
		let httpParams = new HttpParams();

		if (type != null) {
			httpParams = httpParams.append('type', type);
		}

		if (key != null) {
			httpParams = httpParams.append('key', key);
		}

		if (settings.options.hasSort && settings.sort.column) {
			httpParams = httpParams.append('sortBy', `${settings.sort.column} ${settings.sort.isAsc ? 'asc' : 'desc'}`);
		}

		if (settings.options.hasPaging) {
			httpParams = httpParams.append('pageNumber', '' + settings.pageNumber).append('pageSize', '' + settings.pageSize);
		}

		return this.http.get<IRessourceLightList>(`${environment.apiUrl}/ressources`, {
			params: httpParams
		});
	}

	addRessource(files: FileList, type: RessourceType, ressourceKey?: string): Observable<any> {
		const formData = new FormData();

		for (let i = 0; i < files?.length; i++) {
			formData.append('File', files[i]);
			formData.append('FileName', files[i].name);
			formData.append('Mime', files[i].type);
			formData.append('RessourceKey', ressourceKey ?? files[i].name);
		}

		formData.append('Type', type.toString());

		return this.http.post(`${environment.apiUrl}/ressources/add`, formData, {
			responseType: 'text'
		});
	}

	deleteRessource(id: string): Observable<any> {
		return this.http.delete<any>(`${environment.apiUrl}/ressources/${id}`);
	}

	downloadRessourceContent(id: string, fileName: string, closeWindowAfter?: boolean) {
		const page = `ressources/${id}/content`;
		const url = `${environment.apiUrl}/${page}`;
		this.toastr.warning(this.translateService.instant('Documents.DownloadDocument.DownloadIsRunning'));
		this.downloadContentBase(
			url,
			this.translateService.instant('Documents.DownloadDocument.DownloadSuccess'),
			closeWindowAfter,
			fileName
		);
	}
}
